import { json } from "@remix-run/node"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react"

import { NewRelicClientScript } from "@ligonier/observability"

import { getEnv } from "./utils/get-env"

import type { LoaderFunctionArgs, SerializeFrom } from "@remix-run/node"
import type { PublicEnvironmentVariables } from "./environment.client"

export type RootLoaderData = SerializeFrom<typeof loader>

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const _ENV = getEnv()
  const ENV: PublicEnvironmentVariables = {
    NODE_ENV: _ENV.NODE_ENV,
    PUBLIC_NEW_RELIC_BROWSER_ACCOUNT_ID:
      _ENV.PUBLIC_NEW_RELIC_BROWSER_ACCOUNT_ID,
    PUBLIC_NEW_RELIC_BROWSER_APPLICATION_ID:
      _ENV.PUBLIC_NEW_RELIC_BROWSER_APPLICATION_ID,
    PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY:
      _ENV.PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY,
  }

  return json({ ENV })
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Layout also renders for the ErrorBoundary, where the root loader may not
  // have run.  `useLoaderData` throws outright in that case, which turns every
  // 404 into a 500 -- `useRouteLoaderData` just returns undefined.
  const data = useRouteLoaderData<RootLoaderData>("root")

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        {data ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
            }}
          />
        ) : null}
        {data && data.ENV.NODE_ENV !== "development" ? (
          <NewRelicClientScript
            accountId={data.ENV.PUBLIC_NEW_RELIC_BROWSER_ACCOUNT_ID}
            applicationId={data.ENV.PUBLIC_NEW_RELIC_BROWSER_APPLICATION_ID}
            licenseKey={data.ENV.PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY}
          />
        ) : null}
      </body>
    </html>
  )
}

export function ErrorBoundary() {
  const error = useRouteError()

  const { status, message } = isRouteErrorResponse(error)
    ? { status: error.status, message: error.statusText || "Not Found" }
    : { status: 500, message: "Unexpected Server Error" }

  return (
    <main style={{ fontFamily: "system-ui, sans-serif", lineHeight: 1.8 }}>
      <h1>
        {status} {message}
      </h1>
    </main>
  )
}

function App() {
  return <Outlet />
}

export default App
